/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CluProjectCreationSteps } from 'src/clu/verticals/cluProjectCreationModal/CluProjectCreationModal';
import { LuisStepper } from 'src/core/components/luisStepper/LuisStepper';
import { LuisStepperStepType } from 'src/core/components/luisStepper/LuisStepperStep';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { ProjectCreationSteps } from 'src/customText/verticals/projectCreationModal/ProjectCreationModal';

export type ProjectCreationStepsNavigatorProps = { activeStep: number; horizontal: boolean; onActiveStepChange: (newStep: number) => void };

const messages = defineMessages({
    basicInfo: { id: 'clu.stepsNavigator.basicInfo', defaultMessage: 'Enter basic information' },
    reviewAndFinish: { id: 'clu.stepsNavigator.reviewAndFinish', defaultMessage: 'Review and finish' }
});

const getStepStatus = (stepIndex: CluProjectCreationSteps, activeStep: CluProjectCreationSteps) => {
    if (stepIndex === activeStep) {
        return 'active';
    }

    return activeStep > stepIndex ? 'completed' : 'none';
};

export const CluModalStepsNavigator = (props: ProjectCreationStepsNavigatorProps) => {
    const { activeStep, horizontal, onActiveStepChange } = props;
    const { localize } = useLocalization();

    const steps: LuisStepperStepType[] = [
        {
            id: 'basicInfo',
            title: localize(messages.basicInfo),
            disabled: activeStep < ProjectCreationSteps.EnterBasicInformation,
            state: getStepStatus(CluProjectCreationSteps.EnterBasicInformation, activeStep)
        },
        {
            id: 'reviewAndFinish',
            title: localize(messages.reviewAndFinish),
            disabled: activeStep < CluProjectCreationSteps.ReviewAndFinish,
            state: getStepStatus(CluProjectCreationSteps.ReviewAndFinish, activeStep)
        }
    ];

    const onStepClick = (stepKey: string) => {
        let step: CluProjectCreationSteps;

        switch (stepKey) {
            case 'basicInfo':
                step = CluProjectCreationSteps.EnterBasicInformation;
                break;
            case 'reviewAndFinish':
            default:
                step = CluProjectCreationSteps.ReviewAndFinish;
                break;
        }

        onActiveStepChange(step);
    };

    return <LuisStepper horizontal={horizontal} steps={steps} onStepClick={onStepClick} />;
};
